// sapi-admin/src/pages/EneoTransactions.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase/config';
import { collection, query, orderBy, onSnapshot} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, Search, Filter, ChevronDown, X, RefreshCw, 
  CheckCircle, XCircle, Clock, Eye,
  Printer, Users,
  DollarSign, Battery
} from 'lucide-react';
import FormatPrice from '../components/FormatPrice';
import { sendWhatsAppTemplate } from '../services/whatsappService';

// ─── Composants UI ────────────────────────────────────────────────────────
const GlassPanel = ({ children, style = {} }) => (
  <div style={{ 
    background: "rgba(17,24,39,.85)", 
    border: "1px solid rgba(255,255,255,.06)", 
    borderRadius: 16, 
    backdropFilter: "blur(16px)", 
    ...style 
  }}>{children}</div>
);

const MiniStat = ({ label, value, accent="#14b8a6", icon:Icon, index=0 }) => (
  <motion.div initial={{ opacity:0, y:18 }} animate={{ opacity:1, y:0 }} transition={{ delay:index*.07, duration:.45, ease:[.22,1,.36,1] }} whileHover={{ y:-3, transition:{ duration:.15 } }}
    style={{ background:"rgba(17,24,39,.85)", border:`1px solid ${accent}33`, borderRadius:14, padding:"14px 16px", boxShadow:`0 0 24px ${accent}0d`, position:"relative", overflow:"hidden" }}>
    <div style={{ position:"absolute", top:-24, right:-24, width:72, height:72, borderRadius:"50%", background:`${accent}18`, filter:"blur(20px)", pointerEvents:"none" }} />
    <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", position:"relative" }}>
      <div>
        <p style={{ fontSize:9, textTransform:"uppercase", letterSpacing:".14em", color:"#475569", marginBottom:8 }}>{label}</p>
        <p style={{ fontSize:22, fontWeight:700, color:"#fff", lineHeight:1 }}>{value}</p>
      </div>
      {Icon && <div style={{ width:32, height:32, borderRadius:9, background:`${accent}18`, border:`1px solid ${accent}33`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><Icon size={15} color={accent} strokeWidth={1.8} /></div>}
    </div>
    <motion.div initial={{ scaleX:0 }} animate={{ scaleX:1 }} transition={{ duration:.5, delay:.2+index*.07 }} style={{ position:"absolute", bottom:0, left:0, right:0, height:2, background:accent, opacity:.5, transformOrigin:"left" }} />
  </motion.div>
);

const Btn = ({ children, onClick, variant = "ghost", size = "sm", disabled = false, style = {} }) => {
  const V = { 
    ghost: { background: "rgba(255,255,255,.06)", color: "#cbd5e1", border: "1px solid rgba(255,255,255,.08)" },
    amber: { background: "rgba(245,158,11,.15)", color: "#f59e0b", border: "1px solid rgba(245,158,11,.3)" },
    violet: { background: "rgba(139,92,246,.15)", color: "#a78bfa", border: "1px solid rgba(139,92,246,.3)" },
    green: { background: "rgba(16,185,129,.15)", color: "#34d399", border: "1px solid rgba(16,185,129,.3)" },
    red: { background: "rgba(239,68,68,.15)", color: "#f87171", border: "1px solid rgba(239,68,68,.3)" }
  };
  const v = V[variant] || V.ghost;
  const pad = size === "xs" ? "3px 8px" : size === "sm" ? "5px 12px" : "8px 18px";
  return (
    <button 
      onClick={onClick} 
      disabled={disabled} 
      style={{ 
        ...v, 
        padding: pad, 
        borderRadius: 8, 
        fontSize: 11, 
        fontWeight: 600, 
        cursor: disabled ? "not-allowed" : "pointer", 
        opacity: disabled ? .45 : 1, 
        display: "inline-flex", 
        alignItems: "center", 
        gap: 5, 
        transition: "opacity .15s", 
        whiteSpace: "nowrap", 
        ...style 
      }}
    >
      {children}
    </button>
  );
};

const StatusBadge = ({ status }) => {
  const config = {
    completed: { bg: "rgba(16,185,129,.12)", color: "#34d399", ring: "rgba(16,185,129,.3)", icon: CheckCircle, text: "Completed" },
    pending: { bg: "rgba(245,158,11,.12)", color: "#fbbf24", ring: "rgba(245,158,11,.3)", icon: Clock, text: "Pending" },
    cancelled: { bg: "rgba(239,68,68,.12)", color: "#f87171", ring: "rgba(239,68,68,.3)", icon: XCircle, text: "Cancelled" },
    approved: { bg: "rgba(139,92,246,.12)", color: "#a78bfa", ring: "rgba(139,92,246,.3)", icon: CheckCircle, text: "Approved" }
  };
  const c = config[status] || config.pending;
  const Icon = c.icon;
  
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      background: c.bg,
      color: c.color,
      boxShadow: `inset 0 0 0 1px ${c.ring}`,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: ".06em",
      textTransform: "uppercase",
      padding: "3px 8px",
      borderRadius: 20
    }}>
      <Icon size={10} />
      {c.text}
    </span>
  );
};

const SegmentBadge = ({ segment }) => {
  const colors = {
    FAMILY: { bg: "rgba(34,197,94,.12)", color: "#22c55e" },
    FAMILY_PLUS: { bg: "rgba(59,130,246,.12)", color: "#3b82f6" },
    BUSINESS: { bg: "rgba(139,92,246,.12)", color: "#a78bfa" },
    PREMIUM_RESIDENTIAL: { bg: "rgba(245,158,11,.12)", color: "#f59e0b" },
    PREMIUM_PROFESSIONAL: { bg: "rgba(236,72,153,.12)", color: "#ec4899" }
  };
  const c = colors[segment] || colors.FAMILY;
  
  return (
    <span style={{
      background: c.bg,
      color: c.color,
      fontSize: 10,
      fontWeight: 600,
      padding: "2px 8px",
      borderRadius: 20,
      display: "inline-block"
    }}>
      {segment}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════
function EneoTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSegment, setFilterSegment] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [stats, setStats] = useState({ total: 0, totalVolume: 0, totalKWh: 0, uniqueUsers: 0 });

  // ✅ Déplacer la fonction ICI (à l'intérieur du composant)
  const handleSendEneoWhatsApp = async (transaction) => {
    if (!transaction.userPhone) {
      toast.warning('❌ Aucun numéro de téléphone');
      return;
    }

    if (!transaction.token || transaction.status !== 'completed') {
      toast.warning('❌ Aucun token à envoyer (paiement non finalisé)');
      return;
    }

    try {
      const result = await sendWhatsAppTemplate(
        transaction.userPhone,
        'eneo_rech',
        [
          transaction.userName || 'Client',      // {{1}} Nom
          transaction.meterNumber,               // {{2}} Compteur
          String(transaction.kWh || 0),          // {{3}} kWh
          String(transaction.amountFCFA || 0),   // {{4}} Montant FCFA
          FormatPrice(transaction.amountPi),     // {{5}} Montant Pi
          transaction.token                      // {{6}} Token
        ],
        'en'
      );

      if (result.success) {
        toast.success(`✅ WhatsApp envoyé à ${transaction.userPhone}`);
      } else {
        toast.error(`❌ Échec: ${result.error}`);
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      toast.error('Erreur lors de l\'envoi WhatsApp');
    }
  };

  // ─── ÉCOUTE EN TEMPS RÉEL ──────────────────────────────────────────────────
  useEffect(() => {
    const q = query(collection(db, 'tokenTransactions'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const txList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTransactions(txList);
      
      // Calculer les statistiques
      const completed = txList.filter(tx => tx.status === 'completed');
      const totalVolume = completed.reduce((sum, tx) => sum + (tx.amountPi || 0), 0);
      const totalKWh = completed.reduce((sum, tx) => sum + (tx.kWh || 0), 0);
      const uniqueUsers = new Set(txList.map(tx => tx.userId)).size;
      
      setStats({
        total: txList.length,
        totalVolume: totalVolume,
        totalKWh,
        uniqueUsers
      });
      setLoading(false);
    }, (error) => {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to load transactions');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const filteredTransactions = useMemo(() => {
    let filtered = [...transactions];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(tx => 
        tx.meterNumber?.toLowerCase().includes(term) ||
        tx.userId?.toLowerCase().includes(term) ||
        tx.userEmail?.toLowerCase().includes(term) ||
        tx.transactionId?.toLowerCase().includes(term)
      );
    }
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(tx => tx.status === filterStatus);
    }
    
    if (filterSegment !== 'all') {
      filtered = filtered.filter(tx => tx.segment === filterSegment);
    }
    
    return filtered;
  }, [transactions, searchTerm, filterStatus, filterSegment]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatPrice = (price) => {
    if (price === undefined || price === null) return '0 π';
    const num = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(num)) return '0 π';
    const fixedNum = num.toFixed(4);
    const trimmedNum = fixedNum.replace(/\.?0+$/, '');
    const parts = trimmedNum.split('.');
    const integer = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const decimal = parts[1];
    return decimal ? `${integer}.${decimal} π` : `${integer} π`;
  };

  const COL = "80px 1fr 120px 100px 100px 120px 100px 100px";

  if (loading && transactions.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 16 }}>
<div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid rgba(244,63,94,.3)', borderTopColor: '#f43f5e', animation: 'spin .8s linear infinite' }} />        <p style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>Loading ENEO transactions…</p>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ paddingBottom: 48 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
          <div>
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.18em', color: '#f43f5e', marginBottom: 4 }}>ENEO Management</p>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3 }}>
              <Zap size={20} color="#f43f5e" strokeWidth={2} /> ENEO Recharges
            </h1>
            <p style={{ fontSize: 11, color: '#475569' }}>Manage and monitor ENEO prepaid meter recharges</p>
          </div>
          <Btn variant="ghost" disabled={loading}>
          <RefreshCw size={13} style={{ animation: loading ? 'spin .8s linear infinite' : 'none' }} />
          {loading ? 'Loading…' : 'Live ✅'}
        </Btn>
        </div>
        
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12 }}>
          <MiniStat label="Total Transactions" value={stats.total} accent="#f59e0b" icon={Zap} index={0} />
          <MiniStat label="Total Volume" value={`${FormatPrice(stats.totalVolume)}`} accent="#34d399" icon={DollarSign} index={1} />
          <MiniStat label="Total Energy" value={`${stats.totalKWh.toLocaleString()} kWh`} accent="#3b82f6" icon={Battery} index={2} />
          <MiniStat label="Unique Users" value={stats.uniqueUsers} accent="#a78bfa" icon={Users} index={3} />
        </div>
      </div>

      {/* Filters */}
      <GlassPanel style={{ marginBottom: 14 }}>
        <button onClick={() => setFiltersOpen(v => !v)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', color: '#e2e8f0' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600 }}>
            <Filter size={13} color="#f59e0b" /> Filters &amp; Search
            {(filterStatus !== 'all' || filterSegment !== 'all' || searchTerm) && (
              <span style={{ background: 'rgba(245,158,11,.2)', color: '#f59e0b', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 20, border: '1px solid rgba(245,158,11,.3)' }}>ACTIVE</span>
            )}
          </span>
          <ChevronDown size={13} style={{ transform: filtersOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s', color: '#475569' }} />
        </button>
        
        <AnimatePresence>
          {filtersOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: .2 }} style={{ overflow: 'hidden' }}>
              <div style={{ padding: '0 16px 16px', borderTop: '1px solid rgba(255,255,255,.05)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginTop: 14 }}>
                  <div>
                    <label style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#475569', display: 'block', marginBottom: 6 }}>Search</label>
                    <div style={{ position: 'relative' }}>
                      <Search size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#475569', pointerEvents: 'none' }} />
                      <input 
                        value={searchTerm} 
                        onChange={e => setSearchTerm(e.target.value)} 
                        placeholder="Meter, user, transaction ID…" 
                        style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 10, padding: '8px 12px 8px 28px', color: '#e2e8f0', fontSize: 12, width: '100%', outline: 'none' }} 
                      />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#475569', display: 'block', marginBottom: 6 }}>Status</label>
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ background: '#111827', border: '1px solid rgba(255,255,255,.1)', borderRadius: 10, padding: '8px 12px', color: '#e2e8f0', fontSize: 12, width: '100%', outline: 'none' }}>
                      <option value="all">All</option>
                      <option value="completed">Completed</option>
                      <option value="pending">Pending</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="approved">Approved</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#475569', display: 'block', marginBottom: 6 }}>Segment</label>
                    <select value={filterSegment} onChange={e => setFilterSegment(e.target.value)} style={{ background: '#111827', border: '1px solid rgba(255,255,255,.1)', borderRadius: 10, padding: '8px 12px', color: '#e2e8f0', fontSize: 12, width: '100%', outline: 'none' }}>
                      <option value="all">All Segments</option>
                      <option value="FAMILY">FAMILY</option>
                      <option value="FAMILY_PLUS">FAMILY PLUS</option>
                      <option value="BUSINESS">BUSINESS</option>
                      <option value="PREMIUM_RESIDENTIAL">PREMIUM RESIDENTIAL</option>
                      <option value="PREMIUM_PROFESSIONAL">PREMIUM PROFESSIONAL</option>
                    </select>
                  </div>
                </div>
                {(filterStatus !== 'all' || filterSegment !== 'all' || searchTerm) && (
                  <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                    <Btn variant="red" size="xs" onClick={() => { setSearchTerm(''); setFilterStatus('all'); setFilterSegment('all'); }}>
                      <X size={11} /> Clear
                    </Btn>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </GlassPanel>

      {/* Table */}
      <GlassPanel>
        <div style={{ display: 'grid', gridTemplateColumns: COL, gap: 8, padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,.05)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#334155', fontWeight: 700, alignItems: 'center' }}>
          <div >Status</div>
          <div style={{ paddingLeft: 30 }}>Meter / User</div>
          <div>Amount</div>
          <div>Energy</div>
          <div>Segment</div>
          <div>Date</div>
          <div>Token</div>
          <div>Actions</div>
        </div>

        {filteredTransactions.length === 0 && !loading && (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <Zap size={32} style={{ margin: '0 auto 12px', opacity: .2, display: 'block', color: '#475569' }} />
            <p style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>No transactions found</p>
          </div>
        )}

        {filteredTransactions.map((tx, idx) => (
          <motion.div 
            key={tx.id} 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            transition={{ delay: Math.min(idx * .02, .3) }}
            style={{ display: 'grid', gridTemplateColumns: COL, gap: 8, padding: '11px 16px', borderBottom: '1px solid rgba(255,255,255,.03)', alignItems: 'center', transition: 'background .15s', cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(245,158,11,.04)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            onClick={() => { setSelectedTx(tx); setShowModal(true); }}
          >
            <div><StatusBadge status={tx.status} /></div>
            <div>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0', marginLeft: 30 }}>{tx.meterNumber}</p>
              <p style={{ fontSize: 9, marginLeft: 30, color: '#475569' }}>{tx.userId?.substring(0, 10)}...</p>
            </div>
            <div><p style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b' }}>{formatPrice(tx.amountPi)}</p></div>
            <div><p style={{ fontSize: 12, color: '#94a3b8' }}>{tx.kWh} kWh</p></div>
            <div><SegmentBadge segment={tx.segment} /></div>
            <div><p style={{ fontSize: 11, color: '#64748b' }}>{formatDate(tx.createdAt)}</p></div>
            <div>
              {tx.token && tx.status === 'completed' ? (
                <p style={{ fontSize: 10, fontFamily: 'monospace', color: '#a78bfa' }}>{tx.token.substring(0, 14)}...</p>
              ) : (
                <span style={{ color: '#475569', fontSize: 10 }}>—</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 5 }} onClick={e => e.stopPropagation()}>
              <Btn size="xs" variant="ghost" onClick={() => { setSelectedTx(tx); setShowModal(true); }}>
                <Eye size={10} />
              </Btn>
              {/* ✅ Bouton WhatsApp — uniquement si le paiement est réellement finalisé */}
              {tx.userPhone && tx.token && tx.status === 'completed' && (
                <Btn size="xs" variant="green" onClick={() => handleSendEneoWhatsApp(tx)}>
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </Btn>
              )}
              {tx.token && tx.status === 'completed' && (
                <Btn size="xs" variant="amber" onClick={() => {
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(`
                    <html><head><title>ENEO Receipt - ${tx.transactionId}</title>
                    <style>body{font-family:monospace;padding:20px;max-width:400px;margin:0 auto}.token{background:#f0f0f0;padding:15px;text-align:center;font-size:16px;font-weight:bold;margin:15px 0}</style>
                    </head><body>
                    <h2>ENEO CAMEROUN</h2>
                    <p>Prepaid Recharge Receipt</p>
                    <div class="token">${tx.token}</div>
                    <p><strong>Meter:</strong> ${tx.meterNumber}</p>
                    <p><strong>Amount:</strong> ${formatPrice(tx.amountPi)}</p>
                    <p><strong>Energy:</strong> ${tx.kWh} kWh</p>
                    <p><strong>Date:</strong> ${new Date(tx.createdAt).toLocaleString()}</p>
                    <button onclick="window.print()">Print</button>
                    </body></html>
                  `);
                  printWindow.document.close();
                }}>
                  <Printer size={10} />
                </Btn>
              )}
            </div>
          </motion.div>
        ))}

        {filteredTransactions.length > 0 && (
          <div style={{ padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,.04)' }}>
            <p style={{ fontSize: 10, color: '#334155' }}>
              Showing <span style={{ color: '#f59e0b', fontWeight: 700 }}>{filteredTransactions.length}</span> of <span style={{ color: '#f59e0b', fontWeight: 700 }}>{transactions.length}</span> transactions
            </p>
          </div>
        )}
      </GlassPanel>

      {/* Modal Détails */}
      <AnimatePresence>
        {showModal && selectedTx && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} 
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 }}
            onClick={() => setShowModal(false)}>
            <motion.div initial={{ scale: .95 }} animate={{ scale: 1 }} exit={{ scale: .95 }}
              style={{ background: '#0d1526', border: '1px solid rgba(255,255,255,.1)', borderRadius: 20, maxWidth: 500, width: '100%', maxHeight: '90vh', overflow: 'auto' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>Transaction Details</h3>
                
                <button onClick={() => setShowModal(false)} style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 8, padding: 5, cursor: 'pointer' }}><X size={14} color="#94a3b8" /></button>
              </div>
              <div style={{ padding: 20 }}>
                <div style={{ marginBottom: 16, textAlign: 'center' }}>
                  <StatusBadge status={selectedTx.status} />
                </div>
                <div style={{ display: 'grid', gap: 12 }}>
                  <div><label style={{ fontSize: 10, color: '#475569' }}>Transaction ID</label><p style={{ fontSize: 12, color: '#e2e8f0', fontFamily: 'monospace' }}>{selectedTx.transactionId || selectedTx.id}</p></div>
                  <div><label style={{ fontSize: 10, color: '#475569' }}>Meter Number</label><p style={{ fontSize: 14, fontWeight: 700, color: '#f59e0b' }}>{selectedTx.meterNumber}</p></div>
                  <div><label style={{ fontSize: 10, color: '#475569' }}>User</label><p style={{ fontSize: 12, color: '#94a3b8' }}>{selectedTx.userEmail || selectedTx.userId}</p></div>
                  <div><label style={{ fontSize: 10, color: '#475569' }}>Segment</label><p><SegmentBadge segment={selectedTx.segment} /></p></div>
                  <div><label style={{ fontSize: 10, color: '#475569' }}>Amount</label><p style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}>{formatPrice(selectedTx.amountPi)}</p></div>
                  <div><label style={{ fontSize: 10, color: '#475569' }}>Energy</label><p style={{ fontSize: 16, fontWeight: 600, color: '#34d399' }}>{selectedTx.kWh} kWh</p></div>
                  {selectedTx.token && selectedTx.status === 'completed' && <div><label style={{ fontSize: 10, color: '#475569' }}>Token</label><p style={{ fontSize: 14, fontFamily: 'monospace', background: '#1e293b', padding: 10, borderRadius: 8, textAlign: 'center', letterSpacing: 2, color: '#34d399' }}>{selectedTx.token}</p></div>}
                  <div><label style={{ fontSize: 10, color: '#475569' }}>Date</label><p style={{ fontSize: 12, color: '#94a3b8' }}>{formatDate(selectedTx.createdAt)}</p></div>
                  {selectedTx.paymentId && <div><label style={{ fontSize: 10, color: '#475569' }}>Payment ID</label><p style={{ fontSize: 10, color: '#64748b', fontFamily: 'monospace' }}>{selectedTx.paymentId}</p></div>}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default EneoTransactions;